/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { useCallback, useState } from 'react'
import { css, jsx } from '@emotion/react'

import { EuiButton, EuiButtonEmpty, EuiText, EuiTourStep } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useLocalStorage } from '@modules/utils/hooks/useLocalStorage'
import LocalStorageKey from '@modules/utils/localStorageKeys'

type StepNumber = 1 | 2 | 3 | 4 | 5

interface Step {
  step: StepNumber
  title: JSX.Element
  content: JSX.Element
}

const steps: Step[] = [
  {
    step: 1,
    title: (
      <FormattedMessage
        id='billing-overview-tour.overview-panel.title'
        defaultMessage='Overview panel'
      />
    ),
    content: (
      <FormattedMessage
        id='billing-overview-tour.overview-panel.content'
        defaultMessage='View your current usage for Serverless projects and Hosted deployments.'
      />
    ),
  },
  {
    step: 2,
    title: (
      <FormattedMessage id='billing-overview-tour.plan-panel.title' defaultMessage='Plan panel' />
    ),
    content: (
      <FormattedMessage
        id='billing-overview-tour.plan-panel.content'
        defaultMessage='Check the details of your current plan. Contact our sales team to manage or review alternative plans.'
      />
    ),
  },
  {
    step: 3,
    title: (
      <FormattedMessage
        id='billing-overview-tour.subscription-panel.title'
        defaultMessage='Subscription panel'
      />
    ),
    content: (
      <FormattedMessage
        id='billing-overview-tour.subscription-panel.content'
        defaultMessage='Review your subscription level.'
      />
    ),
  },
  {
    step: 4,
    title: (
      <FormattedMessage
        id='billing-overview-tour.payment-panel.title'
        defaultMessage='Payment panel'
      />
    ),
    content: (
      <FormattedMessage
        id='billing-overview-tour.payment-panel.content'
        defaultMessage='See your next bill cycle and view your billing history. Update your payment method or contact us for other forms of payment.'
      />
    ),
  },
  {
    step: 5,
    title: (
      <FormattedMessage
        id='billing-overview-tour.credits-panel.title'
        defaultMessage='Available credits panel'
      />
    ),
    content: (
      <FormattedMessage
        id='billing-overview-tour.credits-panel.content'
        defaultMessage='Add or review your current, past or future prepaid credits. Review prepaid consumption options for available annual discount options.'
      />
    ),
  },
]

const useOverviewTour = () => {
  const [currentStep, setCurrentStep] = useState<StepNumber>(1)
  const [isTourSeen, setIsTourSeen] = useLocalStorage(
    LocalStorageKey.isBillingOverviewTourSeen,
    false,
  )
  const markTourAsSeen = useCallback(() => setIsTourSeen(true), [setIsTourSeen])

  const onNext = useCallback((step) => {
    setCurrentStep(step + 1)
  }, [])

  const getFooterActions = useCallback(
    (step: StepNumber) => {
      if (step === steps.length) {
        return (
          <EuiButtonEmpty size='s' color='text' onClick={markTourAsSeen}>
            <FormattedMessage id='billing-overview-tour.close-tour' defaultMessage='Close tour' />
          </EuiButtonEmpty>
        )
      }

      return [
        <EuiButtonEmpty size='s' color='text' onClick={markTourAsSeen}>
          <FormattedMessage id='billing-overview-tour.skip' defaultMessage='Skip tour' />
        </EuiButtonEmpty>,
        <EuiButton size='s' onClick={() => onNext(step)}>
          <FormattedMessage id='billing-overview-tour.next' defaultMessage='Next' />
        </EuiButton>,
      ]
    },
    [markTourAsSeen, onNext],
  )

  const TourStep = useCallback(
    ({ step, children }: { step: StepNumber; children: JSX.Element }) => (
      <EuiTourStep
        content={
          <EuiText size='s' color='subdued'>
            {steps[step - 1]?.content}
          </EuiText>
        }
        title={steps[step - 1]?.title}
        subtitle={
          <FormattedMessage id='billing-overview-tour.subtitle' defaultMessage='New experience' />
        }
        isStepOpen={!isTourSeen && currentStep === step}
        maxWidth={350}
        onFinish={markTourAsSeen}
        step={step}
        stepsTotal={steps.length}
        anchorPosition='upCenter'
        zIndex={1000}
        footerAction={getFooterActions(step)}
        css={css({ display: 'flex', flexGrow: 1, flexDirection: 'column' })}
      >
        {children}
      </EuiTourStep>
    ),
    [currentStep, getFooterActions, isTourSeen, markTourAsSeen],
  )

  return { TourStep }
}

export default useOverviewTour
